import React, { useEffect, useRef } from "react";
import { IoClose } from "react-icons/io5";
import { MdOutlineRoute } from "react-icons/md";

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A pop-up on top of the route sheet (Cancel run, Move stops), drawn inside
 * the sheet's own layer: a sheet from the bottom on phones, a centred panel
 * from `sm`. Esc and × call `onClose` unless `closeDisabled` (while it
 * saves). Esc stops here, so it never closes the route sheet under it. Focus
 * moves in when it opens (to the element marked data-autofocus, else the
 * first control), Tab keeps it inside, and it goes back where it was after.
 */
const SheetDialog = ({
  open,
  labelId,
  title,
  subtitle,
  icon: Icon = MdOutlineRoute,
  widthClass = "max-w-[520px]",
  closeDisabled = false,
  onClose,
  footer,
  children,
}) => {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const before = document.activeElement;
    const panel = panelRef.current;
    (
      panel?.querySelector("[data-autofocus]") ??
      panel?.querySelector(FOCUSABLE) ??
      panel
    )?.focus();
    return () => {
      if (before?.isConnected) before.focus();
    };
  }, [open]);

  if (!open) return null;

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      // The route sheet closes on Esc too: this pop-up takes it first.
      event.stopPropagation();
      if (!closeDisabled) onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const items = [...panelRef.current.querySelectorAll(FOCUSABLE)];
    if (!items.length) return;
    const at = items.indexOf(document.activeElement);
    if (event.shiftKey && at <= 0) {
      event.preventDefault();
      items[items.length - 1].focus();
    } else if (!event.shiftKey && (at === -1 || at === items.length - 1)) {
      event.preventDefault();
      items[0].focus();
    }
  };

  return (
    <div
      onKeyDown={onKeyDown}
      className="fixed inset-0 z-[10] flex items-end justify-center bg-black/40 pt-[10px] font-manrope text-[12px] text-ui-text sm:items-center sm:p-[14px] print:hidden"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        tabIndex={-1}
        className={`flex max-h-[calc(100dvh-10px)] w-full ${widthClass} flex-col overflow-hidden rounded-t-[16px] border-[1px] border-b-0 border-ui-border bg-ui-page shadow-ui-md focus:outline-none sm:max-h-[calc(100dvh-28px)] sm:rounded-[11px] sm:border-b-[1px]`}
      >
        <header className="flex shrink-0 items-center gap-[11px] border-b-[1px] border-ui-border bg-ui-surface px-[14px] py-[12px]">
          <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[8px] bg-brand-light text-brand">
            <Icon className="h-[18px] w-[18px]" />
          </span>
          <div className="grid min-w-[0px] flex-1 gap-[2px]">
            <h3
              id={labelId}
              className="m-[0px] font-jakarta text-[15px] font-extrabold leading-[1.25] tracking-[-0.01em] text-ui-text"
            >
              {title}
            </h3>
            {subtitle && (
              <span className="truncate text-[11px] font-medium leading-[1.35] text-ui-text-muted">
                {subtitle}
              </span>
            )}
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={closeDisabled}
            className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[8px] text-ui-text-subtle hover:bg-ui-surface-sunken hover:text-ui-text disabled:cursor-not-allowed disabled:opacity-40"
          >
            <IoClose className="h-[18px] w-[18px]" />
          </button>
        </header>
        <div className="flex min-h-[0px] flex-1 flex-col gap-[10px] overflow-auto p-[12px] [&>*]:shrink-0">
          {children}
        </div>
        {footer && (
          <footer className="flex shrink-0 flex-wrap items-center justify-end gap-[8px] border-t-[1px] border-ui-border bg-ui-surface px-[14px] pb-[calc(12px+env(safe-area-inset-bottom,0px))] pt-[10px] sm:py-[10px]">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
};

export default SheetDialog;
